/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { type PropsWithChildren } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = {
	table: css({
		width: '100%',
		maxHeight: '100%',
		borderCollapse: 'collapse',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		overflowY: 'auto',
		display: 'block',

		'-ms-overflow-style': 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	}),
	thead: css({
		display: 'table-header-group',
		position: 'sticky',
		top: 0,
		zIndex: 1,
	}),
	headerRow: css({
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface.sunken'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	}),
	thWork: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		// Reset browser default <th> padding that inflates row height
		paddingBlock: 0,
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		// there is a top-level style that has very high-specificty that targets
		// the first cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingLeft: `${token('space.100')} !important`,
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body'),
		textAlign: 'left',
	}),
	thFixed: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		// Reset browser default <th> padding that inflates row height
		paddingBlock: 0,
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body'),
		textAlign: 'left',
		boxSizing: 'border-box',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
		whiteSpace: 'nowrap',
	}),
	tbody: css({
		display: 'table-row-group',
	}),
	tfoot: css({
		display: 'table-footer-group',
		position: 'sticky',
		bottom: `calc(-1 * ${token('border.width')})`,
		zIndex: 1,
	}),
	footerRow: css({
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	}),
	footerCell: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		textAlign: 'right',
		paddingInlineEnd: token('space.150'),
		font: token('font.body'),
		color: token('color.text.subtle'),
	}),
	tdLast: css({
		// there is a top-level style that has very high-specificty that targets
		// the last cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingRight: `${token('space.100')} !important`,
	}),
};

const TableHeader = () => {
	const { formatMessage } = useIntl();

	return (
		<thead css={styles.thead}>
			<tr css={styles.headerRow}>
				<th css={styles.thWork}>{formatMessage(messages.workColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.statusColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.assigneeColumnHeader)}</th>
				<th css={styles.thFixed}>{formatMessage(messages.labelsColumnHeader)}</th>
				<th css={[styles.thFixed, styles.tdLast]}>
					{formatMessage(messages.priorityColumnHeader)}
				</th>
			</tr>
		</thead>
	);
};

type TableFooterProps = {
	/** Number of results currently shown. */
	shown: number;
	/** Total number of results. Renders as "X+" when total exceeds shown. */
	total: number;
};

const TableFooter = ({ shown, total }: TableFooterProps) => {
	const totalLabel = total > shown ? `${total.toLocaleString()}+` : total.toLocaleString();

	return (
		<tfoot css={styles.tfoot}>
			<tr css={styles.footerRow}>
				<td css={[styles.footerCell, styles.tdLast]} colSpan={5}>
					{shown} of {totalLabel}
				</td>
			</tr>
		</tfoot>
	);
};

type SearchResultsLiteIssueTableProps = PropsWithChildren<{
	/** Number of rows currently rendered. */
	shownCount: number;
	/** Total number of matching results. */
	totalCount: number;
}>;

export const SearchResultsLiteIssueTable = ({
	children,
	shownCount,
	totalCount,
}: SearchResultsLiteIssueTableProps) => (
	<table css={styles.table} data-testid="search-results-lite-issue-table">
		<TableHeader />
		<tbody css={styles.tbody}>{children}</tbody>
		<TableFooter shown={shownCount} total={totalCount} />
	</table>
);
